import { useState } from 'react';
import { X, Plus, Minus, CheckCircle } from 'lucide-react';
import { useAddToCollection } from '@/hooks/useApi';
import CardImage from '@/components/CardImage';
import toast from 'react-hot-toast';

interface Card {
  id: string;
  name: string;
  set_name?: string;
  game?: string;
  card_number?: string;
  image_url?: string;
}

interface Props {
  card: Card;
  onClose: () => void;
  onSuccess?: () => void;
}

const CONDITIONS = ['Mint', 'Near Mint', 'Excellent', 'Good', 'Played', 'Poor'] as const;

export default function AddToCollectionModal({ card, onClose, onSuccess }: Props) {
  const [condition, setCondition] = useState<string>('Near Mint');
  const [quantity, setQuantity] = useState(1);
  const [acquisitionPrice, setAcquisitionPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [isForTrade, setIsForTrade] = useState(false);
  const [success, setSuccess] = useState(false);

  const { mutate: addCard, isLoading } = useAddToCollection();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addCard(
      {
        card_id:           card.id,
        condition,
        quantity,
        acquisition_price: acquisitionPrice ? Number(acquisitionPrice) : null,
        notes:             notes.trim() || null,
        is_for_trade:      isForTrade,
      },
      {
        onSuccess: () => {
          setSuccess(true);
          toast.success(`${card.name} added to your collection!`);
          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 1200);
        },
        onError: () => { toast.error('Failed to add card'); },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-zinc-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-zinc-800">
          <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100">Add to Collection</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="py-12 flex flex-col items-center text-center px-6">
            <CheckCircle className="h-16 w-16 text-emerald-500 mb-3" />
            <p className="font-semibold text-gray-800 dark:text-zinc-200 text-lg">Added!</p>
            <p className="text-gray-500 dark:text-zinc-500 text-sm mt-1">
              {quantity}× <strong>{card.name}</strong> ({condition})
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Card preview */}
            <div className="flex items-center gap-3 px-5 py-4 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800">
              <div className="h-14 w-10 rounded-lg overflow-hidden flex-shrink-0">
                <CardImage
                  src={undefined}
                  alt={card.name}
                  cardName={card.name}
                  cardNumber={card.card_number}
                  game={card.game}
                  className="w-full h-full"
                />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 dark:text-zinc-100 truncate">{card.name}</p>
                {card.set_name && (
                  <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">
                    {card.game && <span className="mr-1">{card.game} ·</span>}
                    {card.set_name}
                  </p>
                )}
              </div>
            </div>

            <div className="px-5 py-5 space-y-5">
              {/* Condition */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                  Condition *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {CONDITIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCondition(c)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                        condition === c
                          ? 'bg-brand-600 text-white border-brand-600'
                          : 'bg-white dark:bg-zinc-900 text-gray-700 dark:text-zinc-300 border-gray-200 dark:border-zinc-800 hover:border-brand-400'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                  Quantity
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 rounded-lg border border-gray-200 dark:border-zinc-800 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 disabled:opacity-40 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(9999, Number(e.target.value))))}
                    min={1}
                    max={9999}
                    className="w-20 text-center border border-gray-200 dark:border-zinc-800 rounded-lg py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.min(9999, quantity + 1))}
                    className="p-2 rounded-lg border border-gray-200 dark:border-zinc-800 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-500 dark:text-zinc-500">copies</span>
                </div>
              </div>

              {/* Acquisition price */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                  What did you pay? <span className="font-normal text-gray-500 dark:text-zinc-500">(optional)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 text-sm">$</span>
                  <input
                    type="number"
                    value={acquisitionPrice}
                    onChange={(e) => setAcquisitionPrice(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-7 pr-4 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                  Notes <span className="font-normal text-gray-500 dark:text-zinc-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Foil, Signed, 1st Edition..."
                  maxLength={200}
                  className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>

              {/* For trade toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isForTrade}
                  onChange={(e) => setIsForTrade(e.target.checked)}
                  className="accent-brand-600 h-4 w-4"
                />
                <span className="text-sm text-gray-700 dark:text-zinc-300">Mark as available for trade</span>
              </label>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-200 dark:border-zinc-800 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Adding…' : 'Add to Collection'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
